import {
  Download,
  File as FileIcon,
  FileText,
  Image as ImageIcon,
  Pencil,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Aviso, Boton } from "../../components/ui/primitivos";
import { cn } from "../../lib/cn";
import {
  type Arte,
  borrarArte,
  borrarPrueba,
  descargarAdjunto,
  type EntidadArte,
  fetchArtes,
  fetchFormatosArte,
  fetchFormatosPrueba,
  fetchPruebas,
  type Prueba,
  renombrarArte,
  subirArte,
  subirPrueba,
} from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { etiquetaDeEstado } from "./estados";

/**
 * Arte y pruebas de impresión (S7) — porteado de `ArteUpload.tsx` y
 * `ArteListItem.tsx` de `imprenta`.
 *
 * De allá vienen el **drag & drop**, la miniatura de las imágenes, el nombre
 * descriptivo editable y la fila con sus acciones.
 *
 * **La miniatura NO usa la URL del endpoint en un `<img src>`.** `imprenta`
 * tiene un `/artes/{id}/preview` que sirve el archivo `inline`; acá el worker
 * manda todo con `Content-Disposition: attachment` a propósito (ADR 0004), para
 * que un PDF con `/JavaScript` no ejecute en el origen de la app. Se baja con
 * `fetch` y se arma un blob URL, que se muestra en un `<img>` — y un `<img>`
 * solo interpreta imágenes.
 */

const esImagen = (mime: string) => mime.startsWith("image/");

function iconoDe(mime: string) {
  if (esImagen(mime)) return ImageIcon;
  if (mime === "application/pdf") return FileText;
  return FileIcon;
}

function pesoLegible(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const fecha = (iso: string) =>
  new Intl.DateTimeFormat("es-AR", { dateStyle: "short", timeStyle: "short" }).format(
    new Date(iso),
  );

/**
 * Miniatura de una imagen, o el icono del formato.
 *
 * El blob se revoca al desmontar: sin eso cada apertura del modal deja un
 * objeto retenido en memoria hasta recargar la página.
 */
function Miniatura({
  tipo,
  id,
  mimeType,
}: {
  tipo: "artes" | "pruebas";
  id: string;
  mimeType: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const Icono = iconoDe(mimeType);

  useEffect(() => {
    if (!esImagen(mimeType)) return;
    let vigente = true;
    let creada: string | null = null;

    descargarAdjunto(tipo, id)
      .then((blob) => {
        if (!vigente) return;
        creada = URL.createObjectURL(blob);
        setUrl(creada);
      })
      .catch(() => {
        // Sin miniatura se cae al icono: que no se vea la previa no es motivo
        // para romper la fila.
      });

    return () => {
      vigente = false;
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [tipo, id, mimeType]);

  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-neutral-100">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-contain" />
      ) : (
        <Icono className="h-5 w-5 text-neutral-400" aria-hidden="true" />
      )}
    </div>
  );
}

/** Baja el archivo y dispara el "guardar como" del browser. */
async function descargar(tipo: "artes" | "pruebas", id: string, nombreArchivo: string) {
  const blob = await descargarAdjunto(tipo, id);
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = nombreArchivo;
  enlace.click();
  URL.revokeObjectURL(url);
}

/**
 * Zona de subida con arrastre.
 *
 * El `<input type="file">` sigue existiendo debajo: el arrastre es un atajo, no
 * la única forma — con teclado no se arrastra nada.
 */
function ZonaSubida({
  extensiones,
  tamanoMaximo,
  subiendo,
  alElegir,
}: {
  extensiones: readonly string[];
  tamanoMaximo: number;
  subiendo: boolean;
  alElegir: (archivo: File) => void;
}) {
  const [arrastrando, setArrastrando] = useState(false);
  const accept = extensiones.map((e) => `.${e}`).join(",");

  return (
    // Un `<label>` y no un `<div>`: la zona entera queda asociada al input, asi
    // que clickear cualquier parte abre el selector y el arrastre es solo un
    // atajo encima de algo que ya funciona con teclado. Con un `div` el handler
    // de drop seria la unica forma de usarlo con mouse y ninguna con teclado.
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setArrastrando(true);
      }}
      onDragLeave={() => setArrastrando(false)}
      onDrop={(e) => {
        e.preventDefault();
        setArrastrando(false);
        const archivo = e.dataTransfer.files?.[0];
        if (archivo) alElegir(archivo);
      }}
      className={cn(
        "block cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors",
        arrastrando ? "border-brand-400 bg-brand-50" : "border-neutral-300 bg-neutral-50",
      )}
    >
      <span className="block text-neutral-600 text-sm">
        {subiendo ? "Subiendo..." : "Arrastrá el archivo acá, o elegilo:"}
      </span>
      <input
        type="file"
        accept={accept}
        disabled={subiendo}
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          if (archivo) alElegir(archivo);
          // Se limpia para poder volver a elegir el MISMO archivo tras un error.
          e.target.value = "";
        }}
        className="mt-2 w-full text-neutral-700 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-neutral-200 file:px-3 file:py-1.5 file:text-neutral-800"
      />
      <span className="mt-1 block text-neutral-500 text-xs">
        Hasta {pesoLegible(tamanoMaximo)}. Se aceptan: {extensiones.join(", ")}.
      </span>
    </label>
  );
}

/**
 * Los artes de una entidad. Sirve para la OT y para el presupuesto: el arte es
 * polimórfico y acompaña al presupuesto cuando se convierte (ADR 0011).
 */
export function SeccionArtes({
  entidadTipo,
  entidadId,
  soloLectura = false,
  alCambiar,
}: {
  entidadTipo: EntidadArte;
  entidadId: string;
  /** Una OT `ELIMINADA` (ADR 0100 B8): se ve y se descarga, no se edita. */
  soloLectura?: boolean;
  /** Avisa al contenedor: sin arte no se planifica, así que el botón cambia. */
  alCambiar?: () => void | Promise<void>;
}) {
  const { can } = useSession();
  const artes = useDatos(() => fetchArtes(entidadTipo, entidadId), `${entidadTipo}:${entidadId}`);
  const formatos = useDatos(fetchFormatosArte);
  const [nombre, setNombre] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renombrando, setRenombrando] = useState<Arte | null>(null);

  const puedeEditar = can("produccion", "arte") && !soloLectura;

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await artes.recargar();
      await alCambiar?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }

  const lista = artes.datos ?? [];

  return (
    <section className="space-y-2">
      {error ? <Aviso>{error}</Aviso> : null}
      {artes.error ? <Aviso>{artes.error}</Aviso> : null}

      {artes.cargando && lista.length === 0 ? (
        <p className="text-neutral-500 text-sm">Cargando...</p>
      ) : lista.length === 0 ? (
        <p className="text-neutral-500 text-sm">
          {/* El texto depende de la entidad: en un presupuesto no hay OT que
              planificar, y el arte adjunto ahi tiene otro sentido — viaja con
              la conversion (ADR 0011). */}
          {entidadTipo === "PRODUCCION"
            ? "Sin arte. La OT no se puede planificar hasta que tenga al menos uno."
            : "Sin arte. Lo que se adjunte acá acompaña al presupuesto y pasa a la OT al convertirlo."}
        </p>
      ) : (
        <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200">
          {lista.map((arte) => (
            <li key={arte.id} className="flex items-center gap-3 px-3 py-2">
              <Miniatura tipo="artes" id={arte.id} mimeType={arte.mimeType} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-neutral-900 text-sm">
                  {arte.nombre ?? arte.nombreArchivo}
                </p>
                <p className="truncate text-neutral-500 text-xs">
                  {arte.nombre ? `${arte.nombreArchivo} · ` : ""}
                  {pesoLegible(arte.tamanoBytes)} · {fecha(arte.creadoEn)}
                  {arte.creadoPor ? ` · ${arte.creadoPor}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Boton
                  variante="fantasma"
                  aria-label={`Descargar ${arte.nombreArchivo}`}
                  onClick={() => accion(() => descargar("artes", arte.id, arte.nombreArchivo))}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                </Boton>
                {puedeEditar ? (
                  <>
                    <Boton
                      variante="fantasma"
                      aria-label={`Renombrar ${arte.nombreArchivo}`}
                      onClick={() => setRenombrando(arte)}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </Boton>
                    <Boton
                      variante="peligro"
                      aria-label={`Eliminar ${arte.nombreArchivo}`}
                      onClick={() => accion(() => borrarArte(arte.id))}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Boton>
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {renombrando ? (
        <div className="flex items-end gap-2 rounded-md border border-neutral-200 p-3">
          <label className="flex-1 text-neutral-600 text-sm">
            <span className="block">Nombre de «{renombrando.nombreArchivo}»</span>
            <input
              defaultValue={renombrando.nombre ?? ""}
              placeholder="frente, espalda, versión fondo oscuro..."
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                const valor = (e.target as HTMLInputElement).value;
                void accion(() => renombrarArte(renombrando.id, valor || null));
                setRenombrando(null);
              }}
              id="nombre-arte"
              className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
            />
          </label>
          <Boton
            onClick={() => {
              const input = document.getElementById("nombre-arte") as HTMLInputElement | null;
              void accion(() => renombrarArte(renombrando.id, input?.value || null));
              setRenombrando(null);
            }}
          >
            Guardar
          </Boton>
          <Boton variante="secundario" onClick={() => setRenombrando(null)}>
            Cancelar
          </Boton>
        </div>
      ) : null}

      {puedeEditar && formatos.datos ? (
        <div className="space-y-2">
          <label className="block text-neutral-600 text-sm">
            <span>Nombre descriptivo (opcional)</span>
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="frente, espalda, versión fondo oscuro..."
              className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
            />
          </label>
          <ZonaSubida
            extensiones={formatos.datos.extensiones}
            tamanoMaximo={formatos.datos.tamanoMaximo}
            subiendo={subiendo}
            alElegir={async (archivo) => {
              setSubiendo(true);
              await accion(async () => {
                await subirArte(entidadTipo, entidadId, archivo, nombre);
                setNombre("");
              });
              setSubiendo(false);
            }}
          />
        </div>
      ) : null}
    </section>
  );
}

/**
 * Las pruebas de impresión de una OT.
 *
 * **Solo se sube con la OT en `REALIZANDO_PRUEBA`** — es la regla del worker
 * (ADR 0029), y acá se refleja: fuera de ese estado se explica por qué en vez
 * de ofrecer un botón que va a dar 409.
 */
export function SeccionPruebas({
  produccionId,
  estado,
  soloLectura = false,
  alCambiar,
}: {
  produccionId: string;
  estado: string;
  /** Una OT `ELIMINADA` (ADR 0100 B8): se ve y se descarga, no se edita. */
  soloLectura?: boolean;
  alCambiar?: () => void | Promise<void>;
}) {
  const { can } = useSession();
  const pruebas = useDatos(() => fetchPruebas(produccionId), produccionId);
  const formatos = useDatos(fetchFormatosPrueba);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const puedeEditar = can("produccion", "arte") && !soloLectura;
  const enMomento = estado === "REALIZANDO_PRUEBA";

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await pruebas.recargar();
      await alCambiar?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }

  const lista: readonly Prueba[] = pruebas.datos ?? [];

  return (
    <section className="space-y-2">
      {error ? <Aviso>{error}</Aviso> : null}

      {lista.length === 0 ? (
        <p className="text-neutral-500 text-sm">Sin pruebas de impresión.</p>
      ) : (
        <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200">
          {lista.map((prueba) => (
            <li key={prueba.id} className="flex items-center gap-3 px-3 py-2">
              <Miniatura tipo="pruebas" id={prueba.id} mimeType={prueba.mimeType} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-neutral-900 text-sm">
                  Prueba v{prueba.version}
                </p>
                <p className="truncate text-neutral-500 text-xs">
                  {prueba.nombreArchivo} · {pesoLegible(prueba.tamanoBytes)} ·{" "}
                  {fecha(prueba.creadoEn)}
                  {prueba.creadoPor ? ` · ${prueba.creadoPor}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Boton
                  variante="fantasma"
                  aria-label={`Descargar prueba v${prueba.version}`}
                  onClick={() =>
                    accion(() => descargar("pruebas", prueba.id, prueba.nombreArchivo))
                  }
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                </Boton>
                {puedeEditar ? (
                  <Boton
                    variante="peligro"
                    aria-label={`Eliminar prueba v${prueba.version}`}
                    onClick={() => accion(() => borrarPrueba(prueba.id))}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Boton>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {puedeEditar && !enMomento ? (
        <p className="text-neutral-500 text-sm">
          La prueba se sube con la OT en <strong>REALIZANDO_PRUEBA</strong>
          {estado === "REALIZANDO_PRUEBA" ? "" : ` (está en ${etiquetaDeEstado(estado)})`}.
        </p>
      ) : null}

      {puedeEditar && enMomento && formatos.datos ? (
        <ZonaSubida
          extensiones={formatos.datos.extensiones}
          tamanoMaximo={formatos.datos.tamanoMaximo}
          subiendo={subiendo}
          alElegir={async (archivo) => {
            setSubiendo(true);
            await accion(() => subirPrueba(produccionId, archivo));
            setSubiendo(false);
          }}
        />
      ) : null}
    </section>
  );
}
